import { useState, type FormEvent } from "react";

interface FeedbackModalProps {
  onClose: () => void;
}

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6f4orghk82orf0ckrg";

export default function FeedbackModal({ onClose }: FeedbackModalProps) {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setSuccessMsg("提交成功，感谢你的反馈！");
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string") body.append(key, value);
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const meta = parsed?.meta as Record<string, unknown> | undefined;
      const rawMsg = meta?.message || parsed?.message || meta?.detail || responseText;
      const serverMsg = typeof rawMsg === "string" ? rawMsg : "";
      const code = parsed?.code;
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setSuccessMsg(serverMsg || "提交成功，感谢你的反馈！");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background-950/70 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative w-full max-w-lg rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="关闭"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-900"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <h2 className="font-heading text-lg font-bold text-foreground-950">提交反馈建议</h2>
        <p className="mt-1.5 text-xs text-foreground-600">
          你的每一条建议都会被认真阅读，帮助我们做得更好。
        </p>

        {status === "success" ? (
          <div className="mt-6 flex flex-col items-center rounded-md border border-primary-500/40 bg-primary-500/10 px-5 py-8 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-check-line text-2xl"></i>
            </span>
            <p className="mt-3 text-sm font-semibold text-foreground-900">提交成功</p>
            <p className="mt-1 text-xs text-foreground-600">{successMsg}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              完成
            </button>
          </div>
        ) : (
          <form data-readdy-form="db6f4orghk82orf0ckrg" onSubmit={handleSubmit} className="mt-5 space-y-4" id="feedback-form">
            <div>
              <label htmlFor="fb-name" className="mb-1.5 block text-xs font-medium text-foreground-700">你的称呼</label>
              <input
                id="fb-name"
                name="name"
                type="text"
                required
                placeholder="请输入你的称呼"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
              />
            </div>

            <div>
              <label htmlFor="fb-email" className="mb-1.5 block text-xs font-medium text-foreground-700">联系邮箱</label>
              <input
                id="fb-email"
                name="email"
                type="email"
                required
                placeholder="便于我们回复你"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
              />
            </div>

            <div>
              <label htmlFor="fb-type" className="mb-1.5 block text-xs font-medium text-foreground-700">反馈类型</label>
              <select
                id="fb-type"
                name="type"
                defaultValue="内容建议"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-950 focus:border-primary-500 focus:outline-none"
              >
                <option value="内容建议">内容建议</option>
                <option value="功能体验">功能体验</option>
                <option value="问题反馈">问题反馈</option>
                <option value="合作洽谈">合作洽谈</option>
              </select>
            </div>

            <div>
              <label htmlFor="fb-content" className="mb-1.5 block text-xs font-medium text-foreground-700">反馈内容</label>
              <textarea
                id="fb-content"
                name="content"
                required
                maxLength={500}
                rows={4}
                placeholder="请描述你的建议或遇到的问题（最多 500 字）"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
              ></textarea>
            </div>

            <input
              className="mango-field-guard"
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            {status === "error" && (
              <p className="flex items-center gap-1.5 rounded-md border border-accent-500/40 bg-accent-500/10 px-3 py-2 text-xs text-accent-400">
                <i className="ri-error-warning-line"></i>
                {formError}
              </p>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-500 hover:text-primary-400"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "submitting" ? "提交中…" : "提交反馈"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}